/**
 * Environment variable access utility
 *
 * Handles runtime env vars for SSR apps:
 * - Server-side: Uses process.env (available at runtime)
 * - Client-side: Uses window.__VITE_ENV__ (injected by server.ts) or falls back to import.meta.env
 *
 * This ensures env vars work correctly in both development and production SSR builds.
 */

/**
 * Get an environment variable value
 * Works in both server-side (SSR) and client-side contexts
 */
export function getEnvVar(key: string): string {
  // Server-side (SSR): use process.env
  if (typeof window === "undefined") {
    return process.env[key] || "";
  }

  // Client-side: check for runtime-injected env vars first (production SSR)
  if (typeof window !== "undefined") {
    const runtimeEnv = (window as { __VITE_ENV__?: Record<string, string> })
      .__VITE_ENV__;
    if (runtimeEnv && runtimeEnv[key]) {
      return runtimeEnv[key];
    }
  }

  // Fall back to import.meta.env (development or client-only builds)
  return (import.meta.env as Record<string, string>)[key] || "";
}

/**
 * Get Clerk publishable key
 */
export function getClerkPublishableKey(): string {
  return getEnvVar("VITE_CLERK_PUBLISHABLE_KEY");
}

/**
 * Canonical Mitt Caire provider-portal URL. Single config point for the portal
 * domain: set VITE_MITTCAIRE_URL to change every link on this site; defaults to
 * the live portal so behavior is unchanged when unset.
 */
export function getMittcaireUrl(): string {
  return (
    getEnvVar("VITE_MITTCAIRE_URL") || "https://sverigeshemtjanst.se/mittcaire"
  );
}

/**
 * Get GraphQL URL
 */
export function getGraphqlUrl(): string {
  return getEnvVar("VITE_GRAPHQL_URL");
}

/**
 * Get GraphQL WebSocket URL
 */
export function getGraphqlWsUrl(): string {
  return getEnvVar("VITE_GRAPHQL_WS_URL");
}

/**
 * Check if Clerk is configured
 */
export function hasClerk(): boolean {
  return Boolean(getClerkPublishableKey());
}

/**
 * Get Clerk satellite domain
 */
export function getClerkDomain(): string {
  return getEnvVar("VITE_CLERK_DOMAIN");
}

/**
 * Check if Clerk satellite mode is enabled
 */
export function isClerkSatellite(): boolean {
  const value = getEnvVar("VITE_CLERK_IS_SATELLITE");
  return value === "true" || value === "1";
}

/** Dashboard links stay on the website origin during local development. */
export function getAppHref(path: string): string {
  return import.meta.env.DEV
    ? path
    : new URL(path, "https://app.caire.se/").toString();
}

/** Local development must not inherit production auth URLs from .env. */
export function getClerkSignInUrl(): string {
  if (import.meta.env.DEV) return "/sign-in";
  return getEnvVar("VITE_CLERK_SIGN_IN_URL").trim() || getAppHref("/sign-in");
}

export function getClerkSignUpUrl(): string {
  if (import.meta.env.DEV) return "/sign-up";
  return getEnvVar("VITE_CLERK_SIGN_UP_URL").trim() || getAppHref("/sign-up");
}
